"use client";

import { useState } from "react";
import {
  ChartStyleFields,
  useChartStyle,
} from "@/components/tool-kit/charts/controls";
import { ChartFigure } from "@/components/tool-kit/charts/figure";
import {
  DataTable,
  Notice,
  PillGroup,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, HINT, LABEL } from "@/components/tool-kit/styles";
import { FlowchartDiagram } from "@/components/tools/diagrams-more/flowchart-diagram";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import {
  FLOW_MARKERS,
  FLOW_SHAPES,
  type FlowWarning,
  flowCounts,
  flowOutline,
  flowRows,
  layoutFlowchart,
  parseFlowchart,
} from "@/lib/tool-kit/diagrams-more/flowchart";
import type { LayeredDirection } from "@/lib/tool-kit/diagrams-more/layered";

/** English strings. A translated content file overrides these through `ui`. */
export const FLOWCHART_MAKER_STRINGS = {
  inputLabel: "Your flowchart — one step per line, arrows for the links",
  inputHint:
    "Lines without arrows are linked top to bottom. A line that starts with an arrow continues from the step above it, which is how a decision gets its branches: -Yes-> Next step. Brackets choose the symbol; a step ending in ? becomes a decision, and Start or End a terminator.",
  exampleText:
    "(Start)\n[/Enter a password/]\nIs it at least 8 characters long?\n-No-> Show an error message -> [/Enter a password/]\n-Yes-> [Save the password]\nSave the password -> (End)",
  exampleTitle: "Password Check",
  /** Words that, alone on a line, make a terminator. English ones always work. */
  terminalWords: ["Start", "Begin", "End", "Stop", "Finish", "Exit"],
  loadExample: "Load the example",
  clear: "Clear",
  stepsDetected: "one:{count} step|other:{count} steps",
  decisionsDetected: "one:{count} decision|other:{count} decisions",
  linksDetected: "one:{count} arrow|other:{count} arrows",
  enterSteps: "Type at least one step to draw the flowchart.",
  directionLabel: "Direction",
  directionDown: "Top to bottom",
  directionRight: "Left to right",
  styleLabel: "Symbol style",
  stylePlain: "Black and white",
  styleColor: "Tinted by symbol",
  symbolsSummary: "Symbols you can use",
  symbolColumn: "Write it as",
  nameColumn: "ISO 5807 symbol",
  useColumn: "Use it for",
  shapeNames: {
    terminal: "Terminator",
    process: "Process",
    decision: "Decision",
    data: "Data (input/output)",
    predefined: "Predefined process",
    preparation: "Preparation",
    database: "Stored data (database)",
    document: "Document",
    connector: "Connector",
  },
  shapeUse: {
    terminal: "Where the flow starts and ends.",
    process: "Any action or operation.",
    decision: "A yes/no question with a branch for each answer.",
    data: "Reading or writing information: input, output.",
    predefined: "A step defined elsewhere, such as a subroutine.",
    preparation: "Setting up before a loop or operation.",
    database: "Data held in a database or store.",
    document: "A document or printed output.",
    connector: "Joins two parts of a chart; write a letter or number.",
  },
  warnings: {
    selfLoop:
      "Line {line}: “{text}” points to itself, so that arrow was left out.",
    repeat:
      "Line {line}: {text} was already linked, so the repeat was left out.",
    emptyStep:
      "Line {line}: an arrow has no step on one side, so it was ignored.",
    noStepAbove:
      "Line {line}: this arrow has no step above to continue from, so it was ignored.",
    tooMany:
      "Line {line}: “{text}” was left out — a flowchart here holds up to {limit} steps.",
  },
  moreWarnings:
    "one:…and {count} more notice.|other:…and {count} more notices.",
  stepColumn: "No.",
  textColumn: "Step",
  symbolNameColumn: "Symbol",
  nextColumn: "Leads to",
  labelsAndStyle: "Title and figure style",
  chartTitle: "Title",
  apaStyle: "APA style figure",
  apaHint:
    "Takes the title out of the image and sets the figure number in bold and the title in italics above it — the APA 7 layout.",
  figureNumber: "Figure number",
  figureNote: "Note (optional)",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy outline",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  showDataTable: "Show the flowchart as a table",
  chartLabel: "Flowchart",
  chartDescription:
    "Flowchart of {steps} and {decisions}, starting at “{first}”.",
} as const;

const LIMIT = 60;
const SHOWN_WARNINGS = 4;

function warningText(
  warning: FlowWarning,
  strings: typeof FLOWCHART_MAKER_STRINGS,
  fmt: (value: number) => string
): string {
  return t(strings.warnings[warning.kind], {
    line: fmt(warning.line),
    text: warning.text,
    limit: fmt(LIMIT),
  });
}

export default function FlowchartMakerWidget() {
  const strings = useToolUi(FLOWCHART_MAKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState<string>(strings.exampleText);
  const [direction, setDirection] = useState<LayeredDirection>("down");
  const [colored, setColored] = useState(false);
  const [style, update] = useChartStyle({
    title: strings.exampleTitle,
    xTitle: "",
    yTitle: "",
    color: "blue",
    valueLabels: false,
    apa: false,
    figureNumber: "1",
    note: "",
  });

  const parse = parseFlowchart(raw, LIMIT, strings.terminalWords);
  const layout =
    parse.nodes.length > 0 ? layoutFlowchart(parse, direction) : null;
  const counts = flowCounts(parse);
  const head = [
    strings.stepColumn,
    strings.textColumn,
    strings.symbolNameColumn,
    strings.nextColumn,
  ];
  const rows = flowRows(parse).map((row) => [
    fmt(row.number),
    row.text,
    strings.shapeNames[row.shape],
    row.next,
  ]);
  const stepsText = tPlural(strings.stepsDetected, locale, counts.steps, {
    count: fmt(counts.steps),
  });
  const decisionsText = tPlural(
    strings.decisionsDetected,
    locale,
    counts.decisions,
    { count: fmt(counts.decisions) }
  );
  const shownWarnings = parse.warnings.slice(0, SHOWN_WARNINGS);
  const hiddenWarnings = parse.warnings.length - shownWarnings.length;

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.inputHint}
        id="flm-input"
        label={strings.inputLabel}
        onChange={setRaw}
        prose
        rows={9}
        value={raw}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setRaw(strings.exampleText)}
          type="button"
        >
          {strings.loadExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setRaw("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <details>
        <summary className={`${LABEL} cursor-pointer`}>
          {strings.symbolsSummary}
        </summary>
        <div className="mt-3">
          <DataTable
            head={[strings.symbolColumn, strings.nameColumn, strings.useColumn]}
            rows={FLOW_SHAPES.map((shape) => [
              FLOW_MARKERS[shape],
              strings.shapeNames[shape],
              strings.shapeUse[shape],
            ])}
          />
        </div>
      </details>
      <div aria-live="polite" className="space-y-2">
        {layout ? (
          <p className={HINT}>
            {stepsText}
            {" · "}
            {decisionsText}
            {" · "}
            {tPlural(strings.linksDetected, locale, counts.links, {
              count: fmt(counts.links),
            })}
          </p>
        ) : (
          <Notice>{strings.enterSteps}</Notice>
        )}
        {shownWarnings.map((warning) => (
          <Notice key={`${warning.kind}-${warning.line}-${warning.text}`}>
            {warningText(warning, strings, fmt)}
          </Notice>
        ))}
        {hiddenWarnings > 0 ? (
          <p className={HINT}>
            {tPlural(strings.moreWarnings, locale, hiddenWarnings, {
              count: fmt(hiddenWarnings),
            })}
          </p>
        ) : null}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <PillGroup
          label={strings.directionLabel}
          name="flm-direction"
          onChange={setDirection}
          options={[
            { value: "down", label: strings.directionDown },
            { value: "right", label: strings.directionRight },
          ]}
          value={direction}
        />
        <PillGroup
          label={strings.styleLabel}
          name="flm-style"
          onChange={(value) => setColored(value === "color")}
          options={[
            { value: "plain", label: strings.stylePlain },
            { value: "color", label: strings.styleColor },
          ]}
          value={colored ? "color" : "plain"}
        />
      </div>
      {layout ? (
        <ChartFigure
          apa={style.apa}
          data={flowOutline(parse)}
          figureNumber={style.figureNumber}
          filename={chartFilename(style.title, "flowchart")}
          labels={strings}
          note={style.note}
          table={{ head, rows }}
          title={style.title}
        >
          <FlowchartDiagram
            colored={colored}
            description={t(strings.chartDescription, {
              steps: stepsText,
              decisions: decisionsText,
              first: parse.nodes[0].text,
            })}
            id="flm-chart"
            label={style.title.trim() || strings.chartLabel}
            layout={layout}
            title={style.apa ? "" : style.title}
          />
        </ChartFigure>
      ) : null}
      <ChartStyleFields
        idPrefix="flm"
        labels={strings}
        style={style}
        update={update}
      />
    </div>
  );
}
